import React, { useEffect, useState } from "react";
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Snackbar, TextField, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { FichaContenedor, FilaFicha } from "../../solicitudCheque/components/FichaSolicitud";
import { agregarAduana, editarAduana, mensajeError } from "../services/ordenCompraService";

const vacio = (cambio) => ({ agente: "", num_factura: "", num_poliza: "", fecha_poliza: "", monto: "", moneda: "0", tipo_cambio: cambio ? String(cambio) : "", credito_fiscal: "" });

// Registro de aduana / factura de una orden de compra (agente despachante, póliza, monto y crédito fiscal).
export default function AduanaFormDialog({ open, ordenId, aduana, cambioOrden, onClose, onGuardada }) {
  const [form, setForm] = useState(vacio(cambioOrden));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(aduana
      ? { agente: aduana.agente, num_factura: aduana.num_factura, num_poliza: aduana.num_poliza, fecha_poliza: aduana.fecha_poliza, monto: String(aduana.monto), moneda: String(aduana.moneda), tipo_cambio: String(aduana.tipo_cambio), credito_fiscal: String(aduana.credito_fiscal) }
      : vacio(cambioOrden));
  }, [open, aduana, cambioOrden]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const sim = form.moneda === "1" ? "$us" : "Bs";
  const texto = (campo, etiqueta, extra = {}) => <TextField fullWidth size="small" hiddenLabel value={form[campo]} onChange={set(campo)} slotProps={{ htmlInput: { maxLength: 100, "aria-label": etiqueta, ...extra } }} />;
  const dinero = (campo, etiqueta) => (
    <TextField
      fullWidth size="small" hiddenLabel value={form[campo]} onChange={set(campo)} placeholder="0.00"
      slotProps={{ input: { startAdornment: <InputAdornment position="start">{sim}</InputAdornment> }, htmlInput: { inputMode: "decimal", "aria-label": etiqueta } }}
    />
  );

  const guardar = async (e) => {
    e.preventDefault();
    if (!(Number(String(form.tipo_cambio).replace(",", ".")) > 0)) return setError("El tipo de cambio debe ser mayor a 0.");
    setGuardando(true);
    setError("");
    try {
      if (aduana) await editarAduana(ordenId, aduana.id, form); else await agregarAduana(ordenId, form);
      onGuardada();
    } catch (err) {
      setError(mensajeError(err, "No se pudo guardar la aduana."));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth component="form" onSubmit={guardar}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 800, py: 1.25 }}>{aduana ? "Modificar aduana" : "Agregar aduana"}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.default", p: 1.5 }}>
        <FichaContenedor>
          <FilaFicha campos={[{ etiqueta: "Agente", control: texto("agente", "Agente despachante") }]} />
          <FilaFicha campos={[
            { etiqueta: "N.º de factura", control: texto("num_factura", "N.º de factura") },
            { etiqueta: "N.º de póliza", control: texto("num_poliza", "N.º de póliza") },
          ]}
          />
          <FilaFicha campos={[
            { etiqueta: "Fecha de póliza", control: <TextField fullWidth size="small" type="date" hiddenLabel value={form.fecha_poliza} onChange={set("fecha_poliza")} slotProps={{ htmlInput: { "aria-label": "Fecha de póliza" } }} /> },
            { etiqueta: "Moneda", control: (
              <ToggleButtonGroup
                exclusive fullWidth size="small" color="primary" value={form.moneda} onChange={(_, v) => v && setForm((f) => ({ ...f, moneda: v }))}
                sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, py: 0.5, gap: 0.5 }, "&& .Mui-selected, && .Mui-selected:hover": { bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 800 } }}
              >
                <ToggleButton value="0">{form.moneda === "0" && <CheckIcon sx={{ fontSize: 16 }} />}Bs</ToggleButton>
                <ToggleButton value="1">{form.moneda === "1" && <CheckIcon sx={{ fontSize: 16 }} />}$us</ToggleButton>
              </ToggleButtonGroup>
            ) },
          ]}
          />
          <FilaFicha campos={[
            { etiqueta: "Tipo de cambio *", control: texto("tipo_cambio", "Tipo de cambio", { inputMode: "decimal" }) },
            { etiqueta: "Monto", control: dinero("monto", "Monto") },
          ]}
          />
          <FilaFicha campos={[{ etiqueta: "Crédito fiscal", control: dinero("credito_fiscal", "Crédito fiscal") }]} />
        </FichaContenedor>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={guardando} sx={{ textTransform: "none", fontWeight: 700 }}>
          {guardando ? <CircularProgress size={20} color="inherit" /> : "Guardar"}
        </Button>
      </DialogActions>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={(_, m) => m !== "clickaway" && setError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ width: "100%" }}>{error}</Alert>
      </Snackbar>
    </Dialog>
  );
}
